import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  if (typeof window !== 'undefined') {
    // Only throw in browser environment, not during build
    throw new Error('Missing Supabase environment variables');
  }
  console.warn('Supabase environment variables not found during build - this is expected');
}


// Browser client for Supabase (single instance to prevent "Multiple GoTrueClient instances" warning)
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // CRITICAL: Set to false for manual session exchange in magic link callback
    // This prevents automatic URL parsing which can interfere with our explicit flow
    detectSessionInUrl: false,
    flowType: 'implicit',
    storageKey: 'ct-auth'  // Unique storage key to avoid collisions
  }
});

export type Database = {
  public: {
    Tables: {
      user_profiles: {
        Row: {
          id: string;
          first_name: string;
          email: string;
          password_hash: string | null;
          is_authenticated: boolean;
          auth_user_id: string | null;
          age: number | null;
          country: string | null;
          gender: string | null;
          occupation: string | null;
          education_level: string | null;
          relationship_status: string | null;
          access_token: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          first_name: string;
          email: string;
          password_hash?: string | null;
          is_authenticated?: boolean;
          auth_user_id?: string | null;
          age?: number | null;
          country?: string | null;
          gender?: string | null;
          occupation?: string | null;
          education_level?: string | null;
          relationship_status?: string | null;
          access_token?: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          first_name?: string;
          email?: string;
          password_hash?: string | null;
          is_authenticated?: boolean;
          auth_user_id?: string | null;
          age?: number | null;
          country?: string | null;
          gender?: string | null;
          occupation?: string | null;
          education_level?: string | null;
          relationship_status?: string | null;
          access_token?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
      test_sessions: {
        Row: {
          id: string;
          user_id: string;
          elevenlabs_conversation_id: string | null;
          status: 'in_progress' | 'completed' | 'abandoned';
          total_questions: number | null;
          last_completed_question: number | null;
          questions_answered: unknown | null;
          full_transcript: string | null;
          conversation_duration: number | null;
          started_at: string;
          completed_at: string | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          elevenlabs_conversation_id?: string | null;
          status?: 'in_progress' | 'completed' | 'abandoned';
          total_questions?: number | null;
          last_completed_question?: number | null;
          questions_answered?: unknown | null;
          full_transcript?: string | null;
          conversation_duration?: number | null;
          started_at?: string;
          completed_at?: string | null;
          created_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          elevenlabs_conversation_id?: string | null;
          status?: 'in_progress' | 'completed' | 'abandoned';
          total_questions?: number | null;
          last_completed_question?: number | null;
          questions_answered?: unknown | null;
          full_transcript?: string | null;
          conversation_duration?: number | null;
          started_at?: string;
          completed_at?: string | null;
          created_at?: string;
        };
      };
      cube_test_elements: {
        Row: {
          id: string;
          session_id: string;
          cube_size: string | null;
          cube_color: string | null;
          cube_material: string | null;
          cube_position: string | null;
          cube_placement: string | null;
          cube_transparent: string | null;
          ladder_material: string | null;
          ladder_position: string | null;
          ladder_height: string | null;
          ladder_climbability: string | null;
          ladder_touching_cube: string | null;
          ladder_distance: string | null;
          horse_color: string | null;
          horse_position: string | null;
          horse_head_direction: string | null;
          horse_personality: string | null;
          horse_equipment: string | null;
          horse_distance: string | null;
          horse_action: string | null;
          flower_count: string | null;
          flower_type: string | null;
          flower_color: string | null;
          flower_position: string | null;
          storm_intensity: string | null;
          storm_location: string | null;
          storm_type: string | null;
          storm_affects_objects: string | null;
          storm_feeling: string | null;
          storm_distance: string | null;
          storm_direction: string | null;
          raw_responses: unknown | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          session_id: string;
          cube_size?: string | null;
          cube_color?: string | null;
          cube_material?: string | null;
          cube_position?: string | null;
          cube_placement?: string | null;
          cube_transparent?: string | null;
          ladder_material?: string | null;
          ladder_position?: string | null;
          ladder_height?: string | null;
          ladder_climbability?: string | null;
          ladder_touching_cube?: string | null;
          ladder_distance?: string | null;
          horse_color?: string | null;
          horse_position?: string | null;
          horse_head_direction?: string | null;
          horse_personality?: string | null;
          horse_equipment?: string | null;
          horse_distance?: string | null;
          horse_action?: string | null;
          flower_count?: string | null;
          flower_type?: string | null;
          flower_color?: string | null;
          flower_position?: string | null;
          storm_intensity?: string | null;
          storm_location?: string | null;
          storm_type?: string | null;
          storm_affects_objects?: string | null;
          storm_feeling?: string | null;
          storm_distance?: string | null;
          storm_direction?: string | null;
          raw_responses?: unknown | null;
          created_at?: string;
        };
        Update: {
          id?: string;
          session_id?: string;
          cube_size?: string | null;
          cube_color?: string | null;
          cube_material?: string | null;
          cube_position?: string | null;
          cube_placement?: string | null;
          cube_transparent?: string | null;
          ladder_material?: string | null;
          ladder_position?: string | null;
          ladder_height?: string | null;
          ladder_climbability?: string | null;
          ladder_touching_cube?: string | null;
          ladder_distance?: string | null;
          horse_color?: string | null;
          horse_position?: string | null;
          horse_head_direction?: string | null;
          horse_personality?: string | null;
          horse_equipment?: string | null;
          horse_distance?: string | null;
          horse_action?: string | null;
          flower_count?: string | null;
          flower_type?: string | null;
          flower_color?: string | null;
          flower_position?: string | null;
          storm_intensity?: string | null;
          storm_location?: string | null;
          storm_type?: string | null;
          storm_affects_objects?: string | null;
          storm_feeling?: string | null;
          storm_distance?: string | null;
          storm_direction?: string | null;
          raw_responses?: unknown | null;
          created_at?: string;
        };
      };
      test_results: {
        Row: {
          id: string;
          session_id: string;
          personality_summary: unknown | null;
          psychological_scores: unknown | null;
          generated_tags: string[] | null;
          executive_summary: string | null;
          key_insights: unknown | null;
          recommendations_summary: string[] | null;
          full_report_json: unknown | null;
          report_pdf_url: string | null;
          processing_status: 'pending' | 'processing' | 'completed' | 'failed';
          processed_at: string | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          session_id: string;
          personality_summary?: unknown | null;
          psychological_scores?: unknown | null;
          generated_tags?: string[] | null;
          executive_summary?: string | null;
          key_insights?: unknown | null;
          recommendations_summary?: string[] | null;
          full_report_json?: unknown | null;
          report_pdf_url?: string | null;
          processing_status?: 'pending' | 'processing' | 'completed' | 'failed';
          processed_at?: string | null;
          created_at?: string;
        };
        Update: {
          id?: string;
          session_id?: string;
          personality_summary?: unknown | null;
          psychological_scores?: unknown | null;
          generated_tags?: string[] | null;
          executive_summary?: string | null;
          key_insights?: unknown | null;
          recommendations_summary?: string[] | null;
          full_report_json?: unknown | null;
          report_pdf_url?: string | null;
          processing_status?: 'pending' | 'processing' | 'completed' | 'failed';
          processed_at?: string | null;
          created_at?: string;
        };
      };
      book_purchases: {
        Row: {
          id: string;
          user_id: string | null;
          stripe_checkout_session_id: string;
          stripe_payment_intent_id: string | null;
          customer_email: string;
          customer_name: string | null;
          amount: number | null;
          currency: string | null;
          status: string | null;
          download_token: string | null;
          created_at: string | null;
          updated_at: string | null;
        };
        Insert: {
          id?: string;
          user_id?: string | null;
          stripe_checkout_session_id: string;
          stripe_payment_intent_id?: string | null;
          customer_email: string;
          customer_name?: string | null;
          amount?: number | null;
          currency?: string | null;
          status?: string | null;
          download_token?: string | null;
          created_at?: string | null;
          updated_at?: string | null;
        };
        Update: {
          id?: string;
          user_id?: string | null;
          stripe_checkout_session_id?: string;
          stripe_payment_intent_id?: string | null;
          customer_email?: string;
          customer_name?: string | null;
          amount?: number | null;
          currency?: string | null;
          status?: string | null;
          download_token?: string | null;
          created_at?: string | null;
          updated_at?: string | null;
        };
      };
    };
  };
};
